import {Component, OnDestroy, OnInit} from '@angular/core';
import { CommonModule } from '@angular/common';
import {IDialogComponent} from "../../../../../../common/dialog/iDialog.component";
import {DialogWithInput} from "../../../../../../common/dialog/dialogWithInput";
import {DialogModule} from "../../../../../../common/dialog/dialog.module";
import {
  SaleGetProductsDistinctActionService
} from "../../../../../impl/service/saleGetProductsDistinctAction.service";
import {
  ServiceContainerComponent
} from "../../../../../../core/component/service-container/service-container.component";
import {SelectElement} from "../../../../../../../components/common/select/select.model";
import {Subscription} from "rxjs";
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {CardModule} from "../../../../../../core/component/card/card.module";
import {FormFieldModule} from "../../../../../../core/component/form-field/form-field.module";
import {SelectModule} from "../../../../../../../components/common/select/select.module";
import {ButtonModule} from "../../../../../../core/component/button/button.module";
import {AmountInputModule} from "../../../../../../../components/common/amount/amount-input/amount-input.module";
import {NumberInputModule} from "../../../../../../../components/common/number/number-input/number-input.module";
import {ProductSummaryModel} from "../../../../../../product/api/entity/productSummary.model";
import {
  SaleUpdateMultipleProductsActionService
} from "../../../../../impl/service/saleUpdateMultipleProductsAction.service";
import {BaseView} from "../../../../../../core/view/base.view";

export interface SaleChangeMultipleProductsPriceDialogInput {
  saleId: number;
}

@Component({
  selector: 'app-sale-change-multiple-products-price-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule, ServiceContainerComponent, CardModule, FormFieldModule, SelectModule, ButtonModule, AmountInputModule, NumberInputModule],
  templateUrl: './sale-change-multiple-products-price-dialog.component.html',
  providers: [
    SaleGetProductsDistinctActionService,
    SaleUpdateMultipleProductsActionService
  ]
})
export class SaleChangeMultipleProductsPriceDialogComponent extends BaseView implements
    IDialogComponent<SaleChangeMultipleProductsPriceDialogInput, void>, OnInit, OnDestroy {
  dialog: DialogWithInput<SaleChangeMultipleProductsPriceDialogInput, void>;

  products: ProductSummaryModel[] =[];
  productElements: SelectElement[] = [];
  formGroup: FormGroup;

  subscriptions: Subscription[] = [];

  constructor(public getProductsService: SaleGetProductsDistinctActionService,
              public updateService: SaleUpdateMultipleProductsActionService,
              private formBuilder: FormBuilder) {
    super();
    this.subscriptions.push(this.getProductsService.result$.subscribe(value => {
      this.productElements = [];
      this.products = [];
      if (value) {
        this.products = value;
        this.productElements = value.map(value1 => new SelectElement(value1.id, value1.label));
      }
    }));

    this.formGroup = this.formBuilder.group({
      productId: this.formBuilder.control(undefined, Validators.required),
      taxPercentage: this.formBuilder.control(undefined, Validators.required),
      unitPriceVatExcluded: this.formBuilder.control(undefined, Validators.required),
      unitPriceVatIncluded: this.formBuilder.control(undefined, Validators.required),
    });

    this.formGroup.get('taxPercentage').valueChanges.subscribe((value) => {
      this.updateUnitPriceVatIncluded();
      this.taxPercentageControl.setValue(this.roundNumber(value), {emitEvent: false});
    });

    this.unitPriceVatExcludedControl.valueChanges.subscribe((value) => {
      this.updateUnitPriceVatIncluded();
      this.unitPriceVatExcludedControl.setValue(this.roundNumber(value), {emitEvent: false});
    });
    this.unitPriceVatIncludedControl.valueChanges.subscribe((value) => {
      this.updateUnitPriceVatExcluded();
      this.unitPriceVatIncludedControl.setValue(this.roundNumber(value), {emitEvent: false});
    });
  }

  ngOnInit(): void {
    this.getProductsService.executeAction({
      id: this.dialog.input.saleId
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  update() {
    this.updateService.executeAction({
      id: this.dialog.input.saleId,
      productId: this.formGroup.value.productId,
      taxPercentage: this.formGroup.value.taxPercentage,
      unitPriceVatExcluded: this.formGroup.value.unitPriceVatExcluded,
      unitPriceVatIncluded: this.formGroup.value.unitPriceVatIncluded
    }).then(() => {
      this.toastControl.presentConfirmation('Les prix ont été changés');
      this.dialog.close();
    }).catch((e) => {
      this.toastControl.presentError(e);
    })
  }

  private roundNumber(nb: number): number {
    return Math.round(nb * 100) / 100;
  }

  productChanged() {
    const selectedProduct = this.products.find(product => product.id === this.formGroup.get('productId').value);
    if (selectedProduct) {
      this.formGroup.patchValue({
        taxPercentage: selectedProduct.taxPercentage,
        unitPriceVatExcluded: selectedProduct.priceVatExcluded,
        unitPriceVatIncluded: selectedProduct.priceVatIncluded,
      });
    }
  }

  updateUnitPriceVatIncluded() {
    const taxPercentage = this.taxPercentageControl.value;
    const price = this.unitPriceVatExcludedControl.value * (1 + (taxPercentage / 100));
    this.unitPriceVatIncludedControl.setValue(this.roundNumber(price), {emitEvent: false});
  }

  updateUnitPriceVatExcluded() {
    const taxPercentage = this.taxPercentageControl.value;
    const ratio = 1 + (taxPercentage / 100);
    const calculatedValue = ratio !== 0 ? (this.unitPriceVatIncludedControl.value / ratio) : 0;
    this.unitPriceVatExcludedControl.setValue(this.roundNumber(calculatedValue), {emitEvent: false});
  }


  get taxPercentageControl(): AbstractControl {
    return this.formGroup.get('taxPercentage');
  }
  get unitPriceVatExcludedControl(): AbstractControl {
    return this.formGroup.get('unitPriceVatExcluded');
  }

  get unitPriceVatIncludedControl(): AbstractControl {
    return this.formGroup.get('unitPriceVatIncluded');
  }
}
